import type { JSX, Ref } from 'react'

import {
  NoteRichEditor,
  useNoteMentionsEnabled,
} from '~publish/components/Notes'
import type { NoteContent } from '~publish/components/Notes'
import type { CreateNoteFailure } from '~publish/hooks/notes/useCreateNote'

import { NotesDismissWarn } from './NotesDismissWarn'
import { usePostNotesContext } from './PostNotesProvider'
import styles from './PostNotes.module.css'

const ERROR_COPY: Record<CreateNoteFailure, string> = {
  validation: 'Enter a Note with no more than 500 characters.',
  limit: 'You have reached the Notes limit.',
  permission: 'You do not have permission to add a Note.',
  request: 'The Note could not save. Try again.',
  closed: 'This thread takes no new Notes.',
}

export function CreatePostNoteForm({
  fieldRef,
}: {
  fieldRef?: Ref<HTMLDivElement>
}): JSX.Element {
  const { create, edit, selectionKey, notes, mentionScope } =
    usePostNotesContext()
  const {
    draft,
    revision,
    changeDraft,
    clearSubmittedDraft,
    submit,
    pending,
    error,
    enabled,
  } = create

  const mentionsEnabled = useNoteMentionsEnabled()
  const empty =
    notes.status === 'ready' &&
    notes.notes.length === 0 &&
    create.confirmedNotes.length === 0
  const placeholder = empty
    ? mentionsEnabled
      ? 'Add a note or @mention'
      : 'Add a note'
    : mentionsEnabled
      ? 'Reply or @mention'
      : 'Reply'
  const onSubmit = (content: NoteContent, clear: () => boolean): void => {
    void submit(content).then((result) => {
      // A mounted editor clears itself and keeps its caret. Otherwise drop
      // the draft here, which remounts a returned editor empty.
      if (result.ok && !clear()) clearSubmittedDraft(content)
      return undefined
    })
  }

  return (
    <div className={styles.composer}>
      <NotesDismissWarn enabled={!edit.draft}>
        <NoteRichEditor
          key={`${selectionKey}:${revision}`}
          initialContent={draft}
          onChange={changeDraft}
          onSubmit={onSubmit}
          mentionScope={mentionScope}
          pending={pending}
          submitBlocked={!enabled}
          collapseWhenIdle
        >
          <NoteRichEditor.Field
            label="Add note"
            fieldRef={fieldRef}
            placeholder={placeholder}
          />
          <NoteRichEditor.Footer>
            <NoteRichEditor.Formatting />
            <NoteRichEditor.Actions>
              <NoteRichEditor.Counter />
              <NoteRichEditor.Submit label="Send note" />
            </NoteRichEditor.Actions>
          </NoteRichEditor.Footer>
          {error && (
            <NoteRichEditor.Error>{ERROR_COPY[error]}</NoteRichEditor.Error>
          )}
        </NoteRichEditor>
      </NotesDismissWarn>
    </div>
  )
}
